import { gql, useApolloClient } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'spec';
import { GET_BANK_ACCOUNTS } from '../constants/aryGraphqlSchemas';
import AryLazyAutoCompleteBase from './AryLazyAutoCompleteBase';

const PAGE_COUNT = 100;

function AryBankAccountsLazyAutoComplete({
  pageCount,
  value,
  onChange,
  currencyCode,
  primitiveIncludedBankIds = [],
  excludedBankIds,
  onBlur,
  excludedMainCurrencies,
  isArchived,
  ...others
}) {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const client = useApolloClient();
  const auth = useAuth();
  const latestRequestIdRef = useRef(0);

  const hasGetBankAccountsAccess = auth.BankAccountsList;

  async function loadOptions(pageIndex, searchText) {
    if (hasGetBankAccountsAccess) {
      setLoading(true);
      const requestId = ++latestRequestIdRef.current;
      const r = await client.query({
        query: gql`
          ${GET_BANK_ACCOUNTS.gqlSchema(
            `
              totalCount
              bankAccounts {
                bankAccountId
                bankName
                currencyCode
              }
          `
          )}
        `,
        variables: {
          bankAccountItem: {
            pagination: {
              pageIndex,
              pageCount: pageCount || PAGE_COUNT
            },
            currencyCode,
            searchText,
            primitiveIncludedBankIds: value
              ? primitiveIncludedBankIds.indexOf(value) === -1
                ? primitiveIncludedBankIds.concat(value)
                : primitiveIncludedBankIds
              : undefined,
            excludedBankIds,
            excludedMainCurrencies: excludedMainCurrencies || [],
            showArchived: isArchived
          }
        },
        fetchPolicy: 'no-cache'
      });
      // Ignore stale responses if a newer query has been made meanwhile
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      const receivedOptions =
        r?.data?.bankingQueries.getBankAccounts.bankAccounts?.map(
          (bankAccount) => ({
            value: bankAccount.bankAccountId,
            label: bankAccount.bankName,
            currencyCode: bankAccount.currencyCode
          })
        );
      const newOptions = (pageIndex === 0 ? [] : options)
        .concat(receivedOptions)
        .concat(
          searchText
            ? []
            : selectedOptions.filter(
                (option) =>
                  receivedOptions
                    .map((receivedOption) => receivedOption.value)
                    .indexOf(option.value) === -1
              )
        );
      setOptions(
        [...new Set(newOptions.map((option) => option.value))].map(
          (optionValue) => newOptions.find((obj) => obj.value === optionValue)
        )
      );
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!value) {
      setSelectedOptions([]);
    } else {
      const selectedObj = options?.find((obj) => obj.value === value) || '';
      setSelectedOptions(selectedObj ? [].concat(selectedObj) : []);
    }
  }, [value]);

  useEffect(() => {
    loadOptions(0, '');
    setSelectedOptions([]);
  }, [
    JSON.stringify(primitiveIncludedBankIds),
    currencyCode,
    JSON.stringify(excludedBankIds),
    JSON.stringify(excludedMainCurrencies),
    isArchived
  ]);

  return (
    <AryLazyAutoCompleteBase
      options={options}
      loading={loading}
      isClearable
      value={options?.find((obj) => obj.value === value) || ''}
      onChange={(selectedObj) => {
        if (selectedObj) {
          setSelectedOptions([].concat(selectedObj));
        }
        onChange(selectedObj);
        // onBlur not call when option selected in list of options immediately
        if (onBlur) {
          setTimeout(() => {
            onBlur();
          }, 0);
        }
      }}
      loadOptions={loadOptions}
      {...others}
    />
  );
}

export default AryBankAccountsLazyAutoComplete;
